import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useRef } from "react";

import {
  useFeatureflags,
  useOrderAutoAssign,
  useOrderFormConfig,
} from "@web-app/api";
import {
  Button,
  Header,
  Label,
  SelectAssignee,
  SelectInput,
  Skeleton,
  Tabs,
  Tooltip,
  useAlert,
} from "@web-app/components";
import { FieldValue, Order, User } from "@web-app/types";
import { getApiErrorMessage } from "@web-app/utils/errorHandler";

import { statusOptions, statusOptionsForIndividualOrder } from "../../utils";

import AdditionalDetailsTab from "./tabs/AdditionalDetailsTab";
import DropoffDetailsTab from "./tabs/DropoffDetailsTab";
import EditConfigDrivenTab from "./tabs/EditConfigDrivenTab";
import OrderDetailsTab from "./tabs/OrderDetailsTab";
import PickupDetailsTab from "./tabs/PickupDetailsTab";

export default function OrderEditPanel({
  assignee,
  order,
  onClose,
  onFieldChange,
}: {
  assignee?: User | null;
  onClose: () => void;
  onFieldChange: (
    property: string,
    value: FieldValue,
    fromTab?: "dropoff" | "pickup",
  ) => Promise<void>;
  order: Order;
}) {
  const { hasFeature, isLoading: isFeatureflagsLoading } = useFeatureflags();
  const { autoAssignOrder } = useOrderAutoAssign();
  const { showAlertAsync } = useAlert();
  const includesPickup = order.type.includes("PICKUP");
  const includesDropoff = order.type.includes("DROPOFF");

  const isFormBuilderEnabled = hasFeature("formBuilder");
  const { data: formConfig, isLoading: isFormConfigLoading } =
    useOrderFormConfig();
  const isConfigLoading =
    isFeatureflagsLoading || (isFormBuilderEnabled && isFormConfigLoading);

  // onFieldChange gets a new identity on every render, so debouncing it
  // directly would rebuild the timer and drop the pending edit. Going through a
  // ref keeps one timer for the life of the panel.
  const latestFieldChange = useRef(onFieldChange);
  useEffect(() => {
    latestFieldChange.current = onFieldChange;
  }, [onFieldChange]);

  const debouncedHandleFieldChange = useMemo(
    () =>
      debounce(
        (property: string, value: FieldValue, fromTab?: "dropoff" | "pickup") =>
          latestFieldChange.current(property, value, fromTab),
        1000,
      ),
    [],
  );

  const pickupConfigFields = useMemo(
    () => formConfig?.fields?.filter((f) => f.detailsType === "pickup") ?? [],
    [formConfig?.fields],
  );
  const dropoffConfigFields = useMemo(
    () => formConfig?.fields?.filter((f) => f.detailsType === "dropoff") ?? [],
    [formConfig?.fields],
  );
  const orderConfigFields = useMemo(
    () => formConfig?.fields?.filter((f) => f.detailsType === "order") ?? [],
    [formConfig?.fields],
  );

  const tabs =
    isFormBuilderEnabled && formConfig
      ? [
          includesPickup
            ? {
                content: (
                  <EditConfigDrivenTab
                    order={order}
                    onFieldChange={onFieldChange}
                    debouncedHandleFieldChange={debouncedHandleFieldChange}
                    fields={pickupConfigFields}
                    detailsType="pickup"
                  />
                ),
                id: "pickup",
                title: (
                  <div className="tw-sidebar-tabs-style">
                    <span>Pickup</span>
                  </div>
                ),
              }
            : null,
          includesDropoff
            ? {
                content: (
                  <EditConfigDrivenTab
                    order={order}
                    onFieldChange={onFieldChange}
                    debouncedHandleFieldChange={debouncedHandleFieldChange}
                    fields={dropoffConfigFields}
                    detailsType="dropoff"
                  />
                ),
                id: "dropoff",
                title: (
                  <div className="tw-sidebar-tabs-style">
                    <span>Drop-off</span>
                  </div>
                ),
              }
            : null,
          {
            content: (
              <EditConfigDrivenTab
                order={order}
                onFieldChange={onFieldChange}
                debouncedHandleFieldChange={debouncedHandleFieldChange}
                fields={orderConfigFields}
                detailsType="order"
              />
            ),
            id: "details",
            title: (
              <div className="tw-sidebar-tabs-style">
                <span>Details</span>
              </div>
            ),
          },
        ]
      : [
          includesPickup
            ? {
                content: (
                  <PickupDetailsTab
                    order={order}
                    debouncedHandleFieldChange={debouncedHandleFieldChange}
                    onFieldChange={onFieldChange}
                  />
                ),
                id: "pickup",
                title: (
                  <div className="tw-sidebar-tabs-style">
                    <span>Pickup</span>
                  </div>
                ),
              }
            : null,
          includesDropoff
            ? {
                content: (
                  <DropoffDetailsTab
                    order={order}
                    onFieldChange={onFieldChange}
                    debouncedHandleFieldChange={debouncedHandleFieldChange}
                  />
                ),
                id: "dropoff",
                title: (
                  <div className="tw-sidebar-tabs-style">
                    <span>Drop-off</span>
                  </div>
                ),
              }
            : null,
          {
            content: (
              <OrderDetailsTab
                order={order}
                onFieldChange={onFieldChange}
                debouncedHandleFieldChange={debouncedHandleFieldChange}
              />
            ),
            id: "details",
            title: (
              <div className="tw-sidebar-tabs-style">
                <span>Details</span>
              </div>
            ),
          },
          {
            content: (
              <AdditionalDetailsTab
                order={order}
                debouncedOnChange={debouncedHandleFieldChange}
              />
            ),
            id: "additional",
            title: (
              <div className="tw-sidebar-tabs-style">
                <span>Additional</span>
              </div>
            ),
          },
        ];

  // Closing inside the debounce window would otherwise throw the edit away, and
  // callers rely on onClose meaning "the save has started".
  const handleClose = useCallback(() => {
    debouncedHandleFieldChange.flush();
    onClose();
  }, [debouncedHandleFieldChange, onClose]);

  const autoAssign = async () => {
    try {
      await showAlertAsync(autoAssignOrder.mutateAsync(order.id), {
        error: (err) => {
          const message = getApiErrorMessage(err, "Auto-assign failed");
          if (message.includes("No available drivers")) {
            return {
              message:
                "No available drivers found nearby. Try manual assignment.",
              title: "Auto Assign",
              type: "error",
            };
          } else if (message.includes("already active")) {
            return {
              message: "Auto-assign is already active for this order.",
              title: "Auto Assign",
              type: "error",
            };
          }
          return {
            message,
            title: "Auto Assign",
            type: "error",
          };
        },
        loading: {
          message: "Searching for available drivers...",
          title: "Auto Assign",
          type: "default",
        },
        success: (result) => {
          if (result.status === "QUEUED") {
            return {
              message:
                "Order queued for auto-assign. Searching for available drivers...",
              title: "Auto Assign",
              type: "success",
            };
          } else if (result.status === "SEARCHING" && result.activeOffer) {
            return {
              message: `Searching for driver — offer sent to ${result.activeOffer.driverName} (${result.activeOffer.distanceKm.toFixed(1)} km away)`,
              title: "Auto Assign",
              type: "success",
            };
          }
          return {
            message: `Driver assigned — ${result.assignedDriver!.name} (${result.assignedDriver!.distanceKm.toFixed(1)} km away)`,
            title: "Auto Assign",
            type: "success",
          };
        },
      });
    } catch {
      // Error already shown via showAlertAsync
    }
  };

  return (
    <div
      data-testid="order-edit-panel"
      className="absolute bg-white h-[100dvh] lg:h-full w-full z-40 flex flex-col overflow-hidden"
    >
      <Header
        title={order.displayId}
        leftActions={
          <Button
            icon="fasChevronLeft"
            onClick={handleClose}
            plain
            className="!p-0 hover:!bg-white"
          />
        }
      />

      <div className="flex flex-col gap-4 px-4 py-2">
        <div className="flex flex-col gap-1">
          <Label>Status</Label>
          <SelectInput
            options={
              order.type === "INDIVIDUAL_PICKUP_AND_DROPOFF"
                ? statusOptionsForIndividualOrder
                : statusOptions
            }
            required
            selected={
              order.status === "TODO" && assignee
                ? "ASSIGNED"
                : order.type === "INDIVIDUAL_PICKUP_AND_DROPOFF" &&
                    order.status === "INPROGRESS"
                  ? `${order.status}_${order?.statusProgress || "PICKUP"}`
                  : order.status
            }
            onChange={(value: string) => {
              const [, statusProgress] = value.split("_");

              if (statusProgress) {
                onFieldChange("statusProgress", value);
              } else {
                onFieldChange("status", value);
              }
            }}
            placeholder="Select status"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label>Assignee</Label>

          <div className="flex gap-2 items-stretch">
            <div className="flex-1">
              <Tooltip
                text={
                  order.routeId
                    ? "You cannot change the assignee for this order because it’s part of a route. Please update the route’s assignee instead."
                    : ""
                }
              >
                <SelectAssignee
                  selectedId={order.assigneeId}
                  onChange={(value) => onFieldChange("assignee", value || "")}
                  disabled={!!order.routeId}
                />
              </Tooltip>
            </div>
            <Button
              size="sm"
              disabled={!!order.routeId || autoAssignOrder.isPending}
              isLoading={autoAssignOrder.isPending}
              onClick={autoAssign}
            >
              Auto Assign
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto mt-2 pb-5 lg:pb-0">
        {isConfigLoading ? (
          <div className="px-4 space-y-3" data-testid="form-config-loader">
            <Skeleton height="h-10" width="w-full" />
            <Skeleton height="h-10" width="w-full" />
            <Skeleton height="h-10" width="w-full" />
            <Skeleton height="h-10" width="w-full" />
          </div>
        ) : (
          /* @ts-expect-error - Tabs expects an array of tabs, but we have some null values */
          <Tabs tabs={tabs.filter(Boolean)} />
        )}
      </div>
    </div>
  );
}
